package com.essntl.components.sidepanel

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.Saver
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.coerceIn
import androidx.compose.ui.unit.dp

@Composable
fun rememberSidePanelState(
    initialSidePanelSize: Dp,
    sidePanelVisible: Boolean = true,
    moveEnabled: Boolean = true,
    panelSizeConstraint: SizeConstraint = SizeConstraint(),
): SidePanelState {
    return rememberSaveable(SidePanelState.Saver) {
        SidePanelState(
            initialSidePanelSize = initialSidePanelSize,
            initialSidePanelSizeRatio = Float.NaN,
            sidePanelVisible = sidePanelVisible,
            moveEnabled = moveEnabled,
            panelSizeConstraint = panelSizeConstraint,
        )
    }
}

@Composable
fun rememberSidePanelState(
    initialSidePanelSizeRatio: Float = .5f,
    sidePanelVisible: Boolean = true,
    moveEnabled: Boolean = true,
    panelSizeConstraint: SizeConstraint = SizeConstraint(),
): SidePanelState {
    return rememberSaveable(SidePanelState.Saver) {
        SidePanelState(
            initialSidePanelSize = 100.dp,
            initialSidePanelSizeRatio = initialSidePanelSizeRatio,
            sidePanelVisible = sidePanelVisible,
            moveEnabled = moveEnabled,
            panelSizeConstraint = panelSizeConstraint,
        )
    }
}

class SidePanelState(
    initialSidePanelSize: Dp,
    initialSidePanelSizeRatio: Float,
    sidePanelVisible: Boolean,
    moveEnabled: Boolean,
    panelSizeConstraint: SizeConstraint,
) {
    var isResizing by mutableStateOf(false)
        private set

    var sidePanelOpened by mutableStateOf(sidePanelVisible)

    var moveEnabled by mutableStateOf(moveEnabled)

    var sidePanelSizeRatio by mutableStateOf(initialSidePanelSizeRatio)
        private set

    var sidePanelSize by mutableStateOf(initialSidePanelSize.coerceIn(panelSizeConstraint.minSize, panelSizeConstraint.maxSize))
        private set

    /**
     * The size constraint for the panel.
     */
    var panelSizeConstraint by mutableStateOf(panelSizeConstraint)

    /**
     * The parent maximum available size.
     * This size is automatically updated by the [SidePanel] composable.
     * It is used to calculate the new panel size when the user is dragging the splitter.
     */
    internal var parentMaxSize: Dp = 0.dp
        set(value) {
            field = value
            changeSidePanelSizeRatio(sidePanelSizeRatio)
        }

    fun changeSidePanelSize(size: Dp) {
        sidePanelSizeRatio = Float.NaN
        sidePanelSize = with(panelSizeConstraint) {
            size.coerceIn(minSize, maxSize)
        }
    }

    fun changeSidePanelSizeRatio(ratio: Float) {
        if (ratio.isNaN()) return
        sidePanelSizeRatio = ratio
        sidePanelSize = with(panelSizeConstraint) {
            (parentMaxSize * ratio).coerceIn(minSize, maxSize)
        }
    }

    /**
     * Dispatches a raw movement delta to the [SidePanelState].
     * This method is used by to update the [SidePanelState] when the user is dragging the splitter.
     *
     * @param delta the new panel position delta.
     */
    fun dispatchRawMovement(delta: Dp) {
        val newSize = sidePanelSize - delta
        changeSidePanelSize(newSize)
    }

    fun dispatchDragStart() {
        isResizing = true
    }

    fun dispatchDragEnd() {
        isResizing = false
    }

    companion object {
        val Saver = Saver<SidePanelState, List<*>>(
            save = {
                listOf(
                    it.sidePanelSize.value,
                    it.sidePanelSizeRatio,
                    it.sidePanelOpened,
                    it.moveEnabled,
                    it.panelSizeConstraint,
                )
            },
            restore = {
                SidePanelState(
                    initialSidePanelSize = (it[0] as Float).dp,
                    initialSidePanelSizeRatio = it[1] as Float,
                    sidePanelVisible = it[2] as Boolean,
                    moveEnabled = it[3] as Boolean,
                    panelSizeConstraint = it[4] as SizeConstraint,
                )
            }
        )
    }
}